<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { APP_EVENTS, emitAppEvent } from '@/Utils/appEventBus'
import FormProgress from '@/components/Atoms/FormProgress.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import { computed, ref, useTemplateRef, getCurrentInstance, onMounted, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import _ from 'lodash'
import { VIDEO_UPLOAD_ACCEPT, MP4_ONLY_ERROR_MESSAGE, hasDisallowedVideoFile } from '@/Utils/videoUploadFormats'
import { loadMediaInfoLib } from '@/Utils/mediaInfoLoader'
import { bytes } from '@/filtersNew'
import Spinner from "@/components/ui/Spinner.vue"
import UserService from "@/services/UserService"
import utils from '@/utils'
import AlertModal from '@/components/modals/AlertModal.vue'
import UploadAlertModal from '@/components/modals/UploadAlertModal.vue'
import UploadAlertDetailedModal from '@/components/modals/UploadAlertDetailedModal.vue'
import SelectLiveToVodModal from '@/components/modals/SelectLiveToVodModal.vue'
import UppyUploader from '@/components/UppyUploader.vue'
import MetricsService from '@/services/MetricsService'
import IconBase from '../icon/IconBase.vue'
import IconUpload from '../icon/IconUpload.vue'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import IconPlus from '../icon/IconPlus.vue'
import IconChevronDown from '../icon/IconChevronDown.vue'
import Badge from '../Badge.vue'
import FormButton from '../Atoms/FormButton.vue'
import IconShare from '../icon/IconShare.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import Alert from '../Atoms/Alert.vue'

const { isVisible, hide: hideLegacyModal } = useLegacyModalBridge('cloud-import-box')
const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const store = useStore()
const { notify } = useNotification()

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		}
	},
	mediaOnline: {
		type: Boolean,
	},
	schedulerSettings: {
		type: Object,
		default() { return {} }
	},
	isOneMinuteBefore: {
		type: Boolean
	},
	streamAlive: {
		type: Boolean
	},
	canEncodeVideos: {
		type: Boolean,
		default: false
	},
	multipleUploads: {
		type: Boolean,
		default: true
	},
	maxFiles: {
		type: Number,
		default: 1
	},
	totalFilesPerStream: {
		type: Number,
		default: 999
	},
	videoFiles: {
		type: Array,
		default: () => []
	},
	mp4Only: {
		type: Boolean,
		default: false
	},
	isDisabledControl: {
		type: Boolean,
		default: false
	},
	isVodTrial: {
		type: Boolean,
		default() { return false }
	},
	vodSubExpiredDays: {
		type: Number,
		default() { return 1 }
	},
	advertisingMediaUpload: {
		type: Boolean,
		default: false
	},
	aioStorageLimit: {
		type: Object,
		default() { return {} }
	},
})

const emit = defineEmits({
	'video-added': () => true,
	'upload-inspected': () => true,
	'upload-enqueue': () => true,
	'upload-dequeue': () => true,
	'video-queue-emptied': () => true,
	'updated': () => true,
	'show-add-recorded': () => true,
})

//TemplateRefs
const uploadInput = useTemplateRef('uploadInput')

const openFilePicker = () => {
	// Computer uploads go through the shared Uppy widget (not for ad media,
	// which uses a separate endpoint).
	if (!props.advertisingMediaUpload) {
		emitAppEvent(APP_EVENTS.OPEN_VOD_UPLOADER, { stream: props.stream })
		return
	}
	const el = uploadInput && 'value' in uploadInput ? uploadInput.value : uploadInput
	if (!el) return

	if (Array.isArray(el)) {
		const first = el[0]
		if (first && typeof first.click === 'function') {
			first.click()
		}
	} else if (typeof el.click === 'function') {
		el.click()
	}
}

// Refs

const mediaInfoLib = ref(null)
const widgetLoaded = ref(false)
const uploadErrorMessage = ref(null)
const maxFileSizeErrorTitle = ref(null)
const maxFileSizeErrorMessage = ref(null)
const alertFileInfo = ref(null)
const maxTotalFileSize = ref(null)
const storageRestricitonsLoaded = ref(false)
const maxFileNumberErrorMessage = ref(null)
const uploadList = ref([])
const availableEncodingPresets = ref([
	{ id: '360p', text: '360p', minHeight: 300 },
	{ id: '480p', text: '480p', minHeight: 440 },
	{ id: '720p', text: '720p', minHeight: 700 },
	{ id: '1080p', text: '1080p', minHeight: 1020 }
])
const filesDragging = ref(false)
const allowMultipleUploads = ref(props.multipleUploads)
const showUploadButton = ref(true)
const cloudImp = ref(false)
const showOptions = ref(false)

// Computed

const subscriptions = computed(() => store.getters['User/subscriptions'])
const uploadFiles = computed(() => store.getters['Ui/uploadFiles'])
const vodStreams = computed(() => store.getters['Streams/vodStreams'])
const isOwner = computed(() => store.getters['User/isOwner'])
const shouldCheckMediaQuality = computed(() => route.name === 'ScheduledStreamManageDashboard')
const activeScheduleSubscription = computed(() => subscriptions.value.find(sub => sub.enabled && sub.category === 'scheduled'))

const hasIncompatibleMediaFiles = computed(() => {
	return uploadFiles.value.some(media => media?.mediaInfo && !isMediaCompatible(media.mediaInfo))
})

const maxVideoFileSize = computed(() => {
	if (props.stream.type === 'vod' && props.aioStorageLimit) {
		// AIO plans does not have max file size setting, so we set it to 1000GB by default
		return 1000
	}
	const maxSize = _.get(activeScheduleSubscription.value, 'definitionOverride.schedulerMaxVideoSize', _.get(activeScheduleSubscription.value, 'package.definition.schedulerMaxVideoSize', false))
	return parseFloat(maxSize)
})

const scheduledStorage = computed(() => {
	const scheduledStorage = _.get(activeScheduleSubscription.value, 'definitionOverride.storage', _.get(activeScheduleSubscription.value, 'package.definition.storage', false))
	return parseFloat(scheduledStorage)
})

const vodStorageLimit = computed(() => parseFloat(store.state.User?.maxVodStorage))

const isTrialSub = computed(() => {
	let isTrialSub = false
	let isPaidSub = false

	subscriptions.value.forEach(sub => {
		try {
			if (['5cd5485126fd039846a3255f', '5ccd92f8152b7f25111c39cf', '5e8db3dcc3db0533322db725',
				'6362114709950a6a642540c4', '6362114709950a6a642540c2', '6362114709950a6a642540c3'].indexOf(sub.package._id) !== -1 && sub.enabled === true) {
				isTrialSub = true
			} else if (sub.enabled === true) {
				isPaidSub = true
			}
		} catch (error) {
			console.error(error)
		}
	})
	return isPaidSub ? false : isTrialSub
})

//Hooks

onMounted(async () => {
	await Promise.all([initMediaInfo(), setupAWSLibs()])
	await initMaxFileSizeData()
	await initVideoUpload()
	await initMaxFileSizeData()
})

// Watch

watch(() => uploadFiles.value.length, (newLength) => {
	if (newLength === 0) {
		showUploadButton.value = true
	}
})

// Methods

const isMediaCompatible = (mediaInfo) => {
	if (!shouldCheckMediaQuality.value) return true

	if (mediaInfo.hasAudioTrack && !mediaInfo.isVbr) return true
	return false
}

const getErrorMessage = (mediaInfo) => {
	let message = ''

	if (mediaInfo.isVbr) {
		message = 'File should be CBR-encoded'
	}

	if (!mediaInfo.hasAudioTrack) {
		if (mediaInfo.isVbr) message += ' and have sound'
		else message = 'File should have sound'
	}

	message += '.'

	return message
}

const onVideoAdded = (videoItem) => {
	emit('video-added', videoItem)
	cloudImp.value = false
	showOptions.value = false
}

const setupAWSLibs = () => {
	const scriptId = 'aws-js'
	if (document.getElementById(scriptId) || typeof window.AWS !== 'undefined') {
		return Promise.resolve()
	}

	return new Promise((resolve) => {
		const scriptEl = document.createElement('script')
		scriptEl.src = 'https://sdk.amazonaws.com/js/aws-sdk-2.663.0.min.js'
		scriptEl.setAttribute('id', scriptId)
		scriptEl.onload = () => resolve()
		scriptEl.onerror = () => {
			console.error('Failed to load AWS SDK')
			resolve()
		}
		document.head.appendChild(scriptEl)
	})
}

const initMediaInfo = async () => {
	try {
		mediaInfoLib.value = await loadMediaInfoLib()
		widgetLoaded.value = true
	} catch (error) {
		console.error('Failed to load MediaInfo library', error)
	}
}

const initMaxFileSizeData = async () => {
	// Check max video file size (GB)
	let totalVodBytes = 0
	const vodStorageTherehold = vodStorageLimit.value
	if (props.stream.type === 'vod' && vodStorageTherehold) {
		const streams = vodStreams.value

		streams.forEach((item) => {
			if (item.type === 'vod' && !item.liveParent) {
				totalVodBytes += item.totalBytes
			}
		})

		if (totalVodBytes > 0) {
			totalVodBytes = totalVodBytes / (1000 ** 3)
		}

		maxTotalFileSize.value = (vodStorageTherehold - totalVodBytes) * (1000 ** 3)
	}

	if (_.isNumber(maxVideoFileSize.value) && props.stream.type === 'scheduled') {
		maxTotalFileSize.value = (maxVideoFileSize.value - totalVodBytes) * (1000 ** 3)
	}
	storageRestricitonsLoaded.value = true
}

const initVideoUpload = async () => {
	const el = uploadInput.value
	if (!el) {
		setTimeout(() => initVideoUpload(), 1000)
		return
	}

	// Prevent uploads when subscription storage has no units left
	if (el.files.length && props.aioStorageLimit && typeof props.aioStorageLimit.unitsLeft === 'number' && props.aioStorageLimit.unitsLeft <= 0) {
		const assignedGB = props.aioStorageLimit.assigned
			? (props.aioStorageLimit.assigned / (1000 ** 3)).toFixed(1)
			: vodStorageLimit.value
		maxFileSizeErrorTitle.value = `Your storage limit has been exceeded`
		maxFileSizeErrorMessage.value = `Your VOD storage limit (${assignedGB}GB) has been exceeded. Upgrade now to increase your video upload capacity.`
		showModal('alert-video-max-size')
		el.value = null
		return
	}

	if (!el.files.length) return
	// Calculate max video files of the scheduled stream
	let maxFiles = props.maxFiles
	maxFiles = props.totalFilesPerStream - props.videoFiles.length - uploadList.value.length

	if (uploadFiles.value?.length + el.files.length > maxFiles) {
		maxFileSizeErrorTitle.value = `You have exceeded the maximum number of files allowed for upload`
		maxFileNumberErrorMessage.value = `You have exceeded the limit (${props.totalFilesPerStream} maximum files allowed) for video files. Upgrade now to increase the number of files you can upload.`
		showModal('alert-video-max-file')
		el.value = null
		return
	}
 
	// Check max video file size (GB)
	let totalVodBytes = 0								// total usage of all active(without deleted/archived) VOD + pseudoVOD(live to vod) 
	let totalRealVodBytes = 0							// total usage of all active(without deleted/archived) VOD

	const vodStorageTherehold = vodStorageLimit.value
	if (props.stream.type === 'vod' && vodStorageTherehold) {
		const streams = vodStreams.value

		streams.forEach((item) => {
			if (item.type === 'vod') {
				totalVodBytes += item.totalBytes
			}
			if (item.type === 'vod' && !item.liveParent) {
				totalRealVodBytes += item.totalBytes
			}
		})
		if (totalVodBytes > 0) {
			totalVodBytes = totalVodBytes / (1000 ** 3)
		}
		if (totalRealVodBytes > 0) {
			totalRealVodBytes = totalRealVodBytes / (1000 ** 3)
		}

		let uploadQueueBytes = 0
		if (Array.isArray(el.files)) {
			el.files.forEach((item) => {
				uploadQueueBytes = uploadQueueBytes + (item.size / (1000 ** 3))
			})
		} else {

			Object.entries(el.files).forEach(
				([key, value]) => {
					// console.log(value.size),
					uploadQueueBytes = uploadQueueBytes + (value.size / (1000 ** 3))
				}
			)
		}


		const uploadQueue = uploadList.value || []
		uploadQueue.forEach((item) => {
			uploadQueueBytes = uploadQueueBytes + (item.file.size / (1000 ** 3))
		})

		if ((totalRealVodBytes + uploadQueueBytes) > vodStorageTherehold) {
			maxFileSizeErrorMessage.value = `Storage capacity cannot exceed ${vodStorageTherehold}GB. Upgrade your plan to continue.`
			showModal('alert-video-max-size')
			el.value = null
			return
		}
	}

	if (props.stream.type === 'scheduled') {
		if (_.isNumber(scheduledStorage.value)) {
			let fileSize = 0
			_.forEach(el.files, (file) => fileSize += (file.size / (1000 ** 3)))
			let userId = store.state.User._id
			// Get storage usage
			if (!isOwner.value) {
				userId = await UserService.getUserId(store.state.User.activeTeam)
			}
			const packageId = activeScheduleSubscription.value.package._id

			const storageUsage = await MetricsService.getSubscriptionStorage(userId, packageId)
			const overStorage = (scheduledStorage.value - (storageUsage?.bytes / (1000 ** 3)) - fileSize) < 0
			if (overStorage) {
				maxFileSizeErrorTitle.value = `Your storage limit has been exceeded`
				maxFileSizeErrorMessage.value = `Your Pre-recorded stream storage limit (${scheduledStorage.value}GB) has been exceeded. Upgrade now to increase your video upload capacity.`
				showModal('alert-video-max-size')
				el.value = null
				return
			}
		}
		if (_.isNumber(maxVideoFileSize.value)) {
			const overMaxSize = _.find(el.files, (file) => (file.size / (1000 ** 3)) > (maxVideoFileSize.value - totalVodBytes))
			if (overMaxSize) {
				maxFileSizeErrorTitle.value = `Exceeds maximum file size limit`
				maxFileSizeErrorMessage.value = `File size exceeds maximum limit. Maximum allowed file size is ${maxVideoFileSize.value}GB. Upgrade plan to upload bigger file sizes.`
				alertFileInfo.value = overMaxSize
				showModal('alert-video-max-size1')
				el.value = null
				return
			}
		}
	}

	// Enforce MP4-only video uploads (shared rule: @/Utils/videoUploadFormats).
	if (props.mp4Only && hasDisallowedVideoFile(el.files)) {
		notify({ group: 'error', text: MP4_ONLY_ERROR_MESSAGE })
		el.value = null
		// Ensure the upload button remains visible after showing the error
		showUploadButton.value = true
		return
	}
	store.dispatch('Ui/addStreamToUpload', props.stream)
	Array.prototype.forEach.call(el.files, file => {
		// check for mime types
		if (file.type.startsWith('video')) {
			onMediaAdd(file)
		}
	})

	el.value = null
}

const computeVideoPrecedence = (video) => {
	let precedence = 0
	let siblingVids = props.videoFiles
	if (video) {
		siblingVids = _.filter(props.videoFiles, v => v.id !== video.id)
	}
	precedence = _.size(siblingVids) + 1

	return precedence
}

const onMediaAdd = (file) => {
	// not allow multiple uploads if disabled

	showOptions.value = false
	if (allowMultipleUploads.value === false && uploadList.value.length > 0) {
		return
	}

	let trimmedName = file.name
	const nameChunks = trimmedName.split('.')
	const extname = nameChunks.length > 1 ? nameChunks.pop() : ''
	let basename = nameChunks.join('.')
	if (basename.length > 20) {
		basename = `${basename.substr(0, 14)} ... ${basename.substr(basename.length - 6)}`
	}
	trimmedName = basename + '.' + extname

	const videoId = props.advertisingMediaUpload
		? `unnamed_ad_${Math.random().toString().slice(2)}`
		: `unnamed_${Math.random().toString().slice(2)}`

	const newVideo = {
		id: videoId,
		fileName: trimmedName,
		fileNameOriginal: file.name,
		bytes: file.size,
		mediaInfo: null,
		precedence: computeVideoPrecedence(),
		enabled: true,
		removing: false,
		statusProcessing: false,

		file,
		uploadable: true,
		uploading: false,
		uploadProgress: 0,
		encodingPresets: [],
		encodingUnitsRequired: 0,
		presetListCollapse: true,
		durationExceeded: false
	}

	store.dispatch('Ui/addFileToUpload', newVideo)
	// const uploadItem = this.uploadList[0];
	const uploadItem = uploadFiles.value[0]
	utils.inspectMediaFile(mediaInfoLib.value, file, info => {
		const durationSec = info.duration
		const durationMins = Math.ceil(
			(durationSec < 60 ? 60 : durationSec) / 60
		)

		const mappedInfo = { ...info, durationMins }
		// uploadItem.mediaInfo = mappedInfo;
		const file = newVideo
		store.dispatch('Ui/addFileData', { uploadItem: file, mappedInfo })
		if (isTrialSub.value && info.duration > 10 * 60) {
			// uploadItem.durationExceeded = true;
			store.dispatch('Ui/addFileData', { uploadItem: file, durationExceeded: true })
		}
		emit('upload-inspected', { upload: uploadItem, handler: 'raw' })

	})

	if (props.multipleUploads === false) {
		showUploadButton.value = false
	}

	emit('upload-enqueue', { upload: newVideo, handler: 'raw' })
}


const cancelVideoUpload = (media) => {
	if (!media) return
	if (media.cancelSource) {
		media.cancelSource.cancel()
	}
	media.uploading = false
}

const removeVideoFile = async (media) => {
	media.removing = true
	emit('upload-dequeue', { upload: media, handler: 'raw' })
	cancelVideoUpload(media)
	const index = uploadList.value.indexOf(media)
	uploadList.value.splice(index, 1)

	if (!uploadList.value.length) {
		emit('video-queue-emptied')
		showUploadButton.value = true
	}
}

const clearUploadList = () => {
	uploadList.value.slice().forEach(vid => removeVideoFile(vid))
}

const selectLiveToVod = async () => {
	if (props.stream.type === 'scheduled') {
		emit('show-add-recorded');
		return;
	}
	showModal('modal-select-live-to-vod')
}

const onChange = () => {
	initVideoUpload()
}

const onUpdate = () => {
	emit('updated')
}

const cloudImportStorageTooltip = computed(() => {
	if (props.stream?.type === 'vod' && props.aioStorageLimit && typeof props.aioStorageLimit.unitsLeft === 'number' && props.aioStorageLimit.unitsLeft <= 0) {
		return 'Cloud import is disabled because your storage limit has been reached. Upgrade your plan to continue.'
	}
	return null
})

const showCloudImportModal = () => {
	if (props.stream?.type === 'vod' && props.aioStorageLimit && typeof props.aioStorageLimit.unitsLeft === 'number' && props.aioStorageLimit.unitsLeft <= 0) {
		return
	}
	showModal('cloud-import-box')
}

const showSharePlaylist = () => showModal('share-playlist') 
</script>

<template>
	<div id="videos-uploader-widget" :class="[props.stream.type + '_uploadbox', { 'w-fit': props.stream.type === 'scheduled' }]">
		<input ref="uploadInput" type="file" :multiple="allowMultipleUploads" style="display: none;" :accept="VIDEO_UPLOAD_ACCEPT"
			name="fields[assetsFieldHandle][]" id="assetsFieldHandle" @change="onChange">
		<div v-if="widgetLoaded" class="uploader-body">
			<div v-if="props.stream.type == 'vod' && !stream.liveParent && !props.advertisingMediaUpload"
				class="flex mb-4 flex-wrap items-start justify-start">
				<drop-down-menu :disabled="vodSubExpiredDays < 0">
					<template #toggle-button>
						<div class="flex items-center gap-x-2">
							<IconBase>
								<IconPlus />
							</IconBase>
							New Video
							<IconBase>
								<IconChevronDown />
							</IconBase>
						</div>
					</template>
					<template #menu-items>
						<drop-down-menu-item
							v-if="!isDisabledControl && showUploadButton && (allowMultipleUploads || (props.videoFiles.length === 0 && uploadList.length === 0))"
							@click="openFilePicker">
							From Your Computer
						</drop-down-menu-item>
						<Tooltip v-if="cloudImportStorageTooltip" :content="cloudImportStorageTooltip">
							<drop-down-menu-item class="flex items-center gap-x-2 inline-block w-full" v-if="!isTrialSub" @click="showCloudImportModal">
								Cloud import
								<Badge title="Beta" />
							</drop-down-menu-item>
						</Tooltip>
						<drop-down-menu-item v-else class="flex items-center gap-x-2" v-if="!isTrialSub" @click="showCloudImportModal">
							Cloud import
							<Badge title="Beta" />
						</drop-down-menu-item>
					</template>
				</drop-down-menu>
				<Tooltip :active="props.videoFiles.length === 0" content="Please upload a video to get the playlist">
					<FormButton :disabled="props.videoFiles.length === 0" type="secondary" @click="showSharePlaylist">
						<div class="flex items-center gap-x-2">
							<IconBase>
								<IconShare />
							</IconBase>
							Share Playlist
							<Badge title="Beta" />
						</div>
					</FormButton>
				</Tooltip>
				<p v-if="isDisabledControl" class="text-danger">Disable schedule to rearrange files</p>
				<FormButton v-if="uploadList.length && allowMultipleUploads" type="danger" class="ml-2 mt-4 lg:mt-0"
					@click="clearUploadList">Remove All</FormButton>
			</div>
			<div v-else-if="props.stream.type == 'scheduled'">
				<div class="flex items-center gap-x-2">
					<Tooltip :active="props.videoFiles.length >= props.totalFilesPerStream"
						:content="`Your plan allows ${props.totalFilesPerStream} video file${props.totalFilesPerStream > 1 ? 's' : ''} per pre-recorded stream. Remove a video or upgrade to upload more.`">
					<FormButton type="secondary" size="sm"
						:disabled="props.streamAlive || isDisabledControl || (!activeScheduleSubscription && stream.type == 'scheduled') || (multipleUploads && isDisabledControl && stream.enabled) || props.videoFiles.length >= props.totalFilesPerStream"
						@click="openFilePicker">
						<div class="flex items-center gap-x-1">
							<IconBase>
								<IconUpload />
							</IconBase>
							Upload video
						</div>
					</FormButton>
					</Tooltip>
					<FormButton v-if="uploadList.length && allowMultipleUploads" type="danger" size="sm" @click="clearUploadList">Remove
						All
					</FormButton>
				</div>
			</div>
			<div v-else>
				<div v-if="!props.stream.liveParent || props.advertisingMediaUpload" class="flex flex-col">
					<div class="flex items-center gap-x-2">
						<h4 v-if="advertisingMediaUpload && videoFiles.length === 0" class="text-sm text-surface-6">Upload the
							video from your device. Maximum length is 2 minutes.</h4>
						<FormButton size="sm"
							v-if="!isDisabledControl && showUploadButton && (allowMultipleUploads || (props.videoFiles.length === 0 && uploadList.length === 0))"
							@click="openFilePicker">
							<span v-if="advertisingMediaUpload">
								Select File
							</span>
							<span v-else-if="!advertisingMediaUpload">
								<IconBase>
									<IconUpload />
								</IconBase>
								Upload
							</span>
						</FormButton>
						<FormButton v-if="uploadList.length && allowMultipleUploads" size="sm" type="danger"
							@click="clearUploadList">
							Remove All</FormButton>
					</div>
					<div v-if="isDisabledControl" class="text-danger">Disable schedule to rearrange files</div>
				</div>
			</div>
			<div class="video-list">
				<Teleport defer to="#scheduled-incompatible-alert" :disabled="props.stream.type !== 'scheduled'">
					<Alert v-if="hasIncompatibleMediaFiles" close-icon
						:class="['text-dark-7 flex !mb-4', props.stream.type === 'scheduled' ? '!mt-3' : '']">
						<div>
							<p class="m-0 text-surface-9 text-sm font-medium">This file doesn't match our recommended settings</p>
							<p class="text-tiny m-0">Playback may be unstable. For best results, re-encode it using our <a
									href="https://docs.castr.com/en/articles/5161326-recommended-settings-for-recorded-videos"
									target="_blank" class="underline text-surface-8">recommended settings</a>. If you'd rather use it
								as-is, run a quick test stream first.</p>
						</div>
					</Alert>
				</Teleport>
				<!-- <ul v-for="media in uploadList" :key="media.name" class="video-item mb-2">
					<li v-if="!media.mediaInfo" class="flex items-center">
						<span :title="media.fileNameOriginal" class="video-name">{{ media.fileName }}</span>
						<i class="fas fa-spinner fa-spin ml-auto" />
					</li>
					<li v-else class="flex items-center gap-x-2 ">
						<div v-if="isMediaCompatible(media.mediaInfo)">
							<span :title="media.fileNameOriginal" class="video-item__name">{{ media.fileName }}</span>
							<div class="video-item__stats text-dark-7 text-s-s">
								<span class="video-item__stats text-dark-7 text-s-s"> {{ media.mediaInfo.width }} x {{
									media.mediaInfo.height }}</span>
								<span v-if="media.mediaInfo.duration > 60"> - {{ media.mediaInfo.durationMins }} mins</span>
								<span v-else> - {{ media.mediaInfo.duration }} secs</span>
								<span> - {{ bytes(media.bytes, false, 0, true) }}</span>
							</div>
							<Badge v-for="(track, index) in media.mediaInfo.codecs" :key="index" class="mr-1 badge_xs"
								:class="track.type" type="main">
								{{ track.codec }}
							</Badge>
						</div>
						<div class="pl-3" v-else>
							<span :title="media.fileNameOriginal" class="video-item__name">{{ media.fileName }}</span>
							<span class="color-warning text-s-s flex">
								<icon-base class="mr-1 text-[#E2A052]" width="16" height="15" width-view-box="16" height-view-box="15">
									<icon-media-file-warning />
								</icon-base>
								<span>{{ getErrorMessage(media.mediaInfo) }}</span>
							</span>
						</div>
						<div class="text-right w-5/12">
							<div v-show="!media.uploading" class="h-full vod-upload-action justify-end items-center">

								 upload button 
								<FormButton title="Click to upload this file" type="main" size="sm" class="px-2"
									@click="uploadVideoFile(media)" :disabled="media.durationExceeded">
									<span v-if="!media.statusProcessing">
										<icon-base fill="none" class="text-white" width="16" height="16" width-view-box="20" height-view-box="20">
											<icon-upload-retry />
										</icon-base>
										<span v-if="!shouldCheckMediaQuality">Upload</span>
									</span>
									<i v-else class="fa fa-spinner fa-spin" />
								</FormButton>

								 remove button 
								<FormButton :key="media.fileName" class="px-1 ml-2" v-show="!media.statusProcessing"
									type="danger" size="sm" @click="removeVideoFile(media)">
									<icon-base fill="none" width="20" height="20" width-view-box="20" height-view-box="20">
										<icon-bucket />
									</icon-base>

								</FormButton>
							</div>
							<div v-show="media.uploading" class="h-full vod-upload-action justify-end items-center">
								<FormProgress :value="media.uploadProgress" :max="100" show-progress animated variant="main"
									class="progressbar-wrapper w-full" />
								<FormButton type="light" :isIcon="true" class="p-0" size="sm" @click="cancelVideoUpload(media)">
									<img src="@/assets/images/close2.svg" alt="close">
								</FormButton>
							</div>
						</div>
						<div class="flex flex-wrap" v-if="media.durationExceeded">
							<div class="text-s-xs text-danger w-full"> Your video exceed 10 mins for trial, please upload shorter
								video.</div>
						</div>
					</li>
				</ul> -->
			</div>
		</div>
		<Spinner v-else text="Retrieving data..." classes="text-dark-8 mt-4 mb-5" spinner-color="var(--c-dark-8)"
			spinner-size="15px" />
		<ModalBox v-model="isVisible" size="md" hide-close header-class="!hidden" wrapper-classes="lg:h-auto lg:max-h-none">

			<UppyUploader v-if="storageRestricitonsLoaded" :stream="props.stream" :max-files="5"
				:max-total-file-size="maxTotalFileSize"
				:total-files-per-stream="totalFilesPerStream"
				:video-files="videoFiles"
				
				:maxFileSize="maxVideoFileSize ? (maxVideoFileSize * (1000 ** 3)) : maxVideoFileSize"
				:storageLimit="aioStorageLimit" @upload-success="onVideoAdded"
				:encodingPresets="canEncodeVideos ? availableEncodingPresets : []" />
		
</ModalBox>
		<alert-modal :message="uploadErrorMessage || 'You are exceeding your storage limit. Please upgrade to continue'"
			modal-id="alert-storage-exceeding" ok-text="Got it, thanks" button-alignment="text-right" />
		<upload-alert-modal modal-id="alert-video-max-size" :message="maxFileSizeErrorTitle"
			:body="maxFileSizeErrorMessage || 'This file is too large. Upload a smaller file or upgrade your plan to continue.'"
			ok-text="Cancel" button-alignment="text-right" />
		<upload-alert-detailed-modal modal-id="alert-video-max-size1" :message="maxFileSizeErrorTitle"
			:body="maxFileSizeErrorMessage || 'This file is too large. Upload a smaller file or upgrade your plan to continue.'"
			:file="alertFileInfo" ok-text="Cancel" button-alignment="text-right" />
		<upload-alert-modal :message="maxFileSizeErrorTitle"
			:body="maxFileNumberErrorMessage || 'You have reached your file limit. Upgrade your plan to upload more.'"
			modal-id="alert-video-max-file" ok-text="Cancel" button-alignment="text-right" />
		<select-live-to-vod-modal :stream="stream" :total-files-per-stream="totalFilesPerStream" :video-files="videoFiles"
			@updated="onUpdate" />
	</div>
</template>


<style scoped>

</style>
